import { useState, useEffect } from 'preact/hooks';
import { getUsers, getDepartments, getLocations, getAllUsers, updateUser, activateUser, deactivateUser, deleteUser, restoreUser, permanentlyDeleteUser, getToil, addToil, searchSlackUsers, importSlackUser, getSlackMemberCounts, bulkImportFromSlack, getAllUserBalances, getLeaveTypes } from '../lib/api';

export function TeamPage({ user, workspace }) {
  const [activeTab, setActiveTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [userBalances, setUserBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ department: '', location: '', search: '', role: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showToilModal, setShowToilModal] = useState(false);
  const [toilUser, setToilUser] = useState(null);
  const [toilBalance, setToilBalance] = useState(null);
  const [toilForm, setToilForm] = useState({ hours: '', reason: '' });
  const [addingToil, setAddingToil] = useState(false);
  // Invite member state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [importing, setImporting] = useState(null);
  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportCounts, setBulkImportCounts] = useState(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportFilter, setBulkImportFilter] = useState('all');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  useEffect(() => {
    loadData();
  }, [activeTab]);

  async function loadData() {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const [usersData, deptsData, locsData, allData, leaveTypesData, balancesData] = await Promise.all([
        getUsers(activeTab),
        getDepartments(),
        getLocations(),
        getAllUsers(),
        getLeaveTypes(),
        getAllUserBalances(currentYear)
      ]);
      setUsers(usersData || []);
      setDepartments(deptsData || []);
      setLocations(locsData || []);
      setAllUsers(allData || []);
      setLeaveTypes(leaveTypesData || []);

      // Organize balances by user_id for easy lookup
      const balancesByUser = {};
      (balancesData || []).forEach(b => {
        if (!balancesByUser[b.user_id]) {
          balancesByUser[b.user_id] = {};
        }
        balancesByUser[b.user_id][b.leave_type_id] = b;
      });
      setUserBalances(balancesByUser);
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setLoading(false);
    }
  }

  // Count users by status
  const counts = {
    active: allUsers.filter(u => u.is_active && !u.is_deleted).length,
    inactive: allUsers.filter(u => !u.is_active && !u.is_deleted).length,
    deleted: allUsers.filter(u => u.is_deleted).length
  };

  const filteredUsers = users.filter(u => {
    if (filter.department && u.department_id !== filter.department) return false;
    if (filter.location && u.location_id !== filter.location) return false;
    if (filter.role && u.role !== filter.role) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      const matchesName = u.name?.toLowerCase().includes(search);
      const matchesEmail = u.email?.toLowerCase().includes(search);
      const matchesSlack = u.slack_id?.toLowerCase().includes(search);
      if (!matchesName && !matchesEmail && !matchesSlack) return false;
    }
    return true;
  });

  async function handleDeactivate(userId) {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await deactivateUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to deactivate user');
    }
  }

  async function handleActivate(userId) {
    try {
      await activateUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to activate user');
    }
  }

  async function handleDelete(userId) {
    if (!confirm('Are you sure you want to delete this user? They will be moved to the Deleted tab.')) return;
    try {
      await deleteUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to delete user');
    }
  }

  async function handleRestore(userId) {
    try {
      await restoreUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to restore user');
    }
  }

  async function handlePermanentDelete(userId, userName) {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE ${userName}?\n\nThis action cannot be undone. All their leave history and balances will be deleted.`)) return;
    // Double confirmation for permanent deletion
    if (!confirm('This is permanent! Click OK to confirm permanent deletion.')) return;
    try {
      await permanentlyDeleteUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to permanently delete user: ' + (err.message || 'Unknown error'));
    }
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function openEditModal(member) {
    // Parse working_days if it's a string, or use null if not set
    let workingDays = null;
    if (member.working_days) {
      try {
        workingDays = typeof member.working_days === 'string'
          ? JSON.parse(member.working_days)
          : member.working_days;
      } catch (e) {
        workingDays = null;
      }
    }

    setEditingUser({
      ...member,
      department_id: member.department_id || '',
      location_id: member.location_id || '',
      manager_id: member.manager_id || '',
      role: member.role || 'user',
      start_date: member.start_date || '',
      working_days: workingDays,
      use_custom_working_days: workingDays !== null
    });
    setShowEditModal(true);
  }

  async function handleSaveUser() {
    if (!editingUser) return;
    setSaving(true);
    try {
      await updateUser(editingUser.id, {
        department_id: editingUser.department_id || null,
        location_id: editingUser.location_id || null,
        manager_id: editingUser.manager_id || null,
        role: editingUser.role,
        start_date: editingUser.start_date || null,
        working_days: editingUser.use_custom_working_days ? editingUser.working_days : null
      });
      setShowEditModal(false);
      setEditingUser(null);
      await loadData();
    } catch (err) {
      alert('Failed to save user');
    } finally {
      setSaving(false);
    }
  }

  async function openToilModal(member) {
    setToilUser(member);
    setToilForm({ hours: '', reason: '' });
    setToilBalance(null);
    setShowToilModal(true);

    // Load current TOIL balance
    try {
      const balance = await getToil(member.id);
      setToilBalance(balance);
    } catch (err) {
      console.error('Failed to load TOIL balance:', err);
    }
  }

  async function handleAddToil() {
    if (!toilUser || !toilForm.hours) return;
    setAddingToil(true);
    try {
      await addToil({
        user_id: toilUser.id,
        hours: parseFloat(toilForm.hours),
        reason: toilForm.reason
      });
      // Reload balance
      const balance = await getToil(toilUser.id);
      setToilBalance(balance);
      setToilForm({ hours: '', reason: '' });
      alert('TOIL hours added successfully');
    } catch (err) {
      alert('Failed to add TOIL: ' + (err.message || 'Unknown error'));
    } finally {
      setAddingToil(false);
    }
  }

  // Invite member functions
  async function openInviteModal() {
    setShowInviteModal(true);
    setInviteSearch('');
    setInviteSearchResults([]);
    // Load all available Slack users initially
    setInviteSearching(true);
    try {
      const result = await searchSlackUsers('');
      setInviteSearchResults(result.users || []);
    } catch (err) {
      console.error('Failed to load Slack users:', err);
    } finally {
      setInviteSearching(false);
    }
  }

  async function handleInviteSearch(query) {
    setInviteSearch(query);
    setInviteSearching(true);
    try {
      const result = await searchSlackUsers(query);
      setInviteSearchResults(result.users || []);
    } catch (err) {
      console.error('Failed to search Slack users:', err);
    } finally {
      setInviteSearching(false);
    }
  }

  async function handleImportUser(slackUser) {
    setImporting(slackUser.id);
    try {
      const result = await importSlackUser(slackUser.id);
      // Remove from search results
      setInviteSearchResults(prev => prev.filter(u => u.id !== slackUser.id));
      // Reload team data
      await loadData();
      alert(result.message || 'User imported successfully');
    } catch (err) {
      alert('Failed to import user: ' + (err.message || 'Unknown error'));
    } finally {
      setImporting(null);
    }
  }

  // Bulk import functions
  async function openBulkImportModal() {
    setShowBulkImportModal(true);
    setBulkImportCounts(null);
    setBulkImportResult(null);
    setBulkImportFilter('all');
    setBulkImportLoading(true);
    try {
      const counts = await getSlackMemberCounts();
      setBulkImportCounts(counts);
    } catch (err) {
      console.error('Failed to load Slack member counts:', err);
    } finally {
      setBulkImportLoading(false);
    }
  }

  function getImportCount() {
    if (!bulkImportCounts) return 0;
    const { slack } = bulkImportCounts;
    if (bulkImportFilter === 'all') return slack.total;
    if (bulkImportFilter === 'full_only') return slack.full;
    if (bulkImportFilter === 'include_multi') return slack.full + slack.multiChannel;
    return 0;
  }

  async function handleBulkImport() {
    setBulkImporting(true);
    setBulkImportResult(null);
    try {
      const result = await bulkImportFromSlack(bulkImportFilter);
      setBulkImportResult(result);
      await loadData();
    } catch (err) {
      setBulkImportResult({ error: err.message || 'Import failed' });
    } finally {
      setBulkImporting(false);
    }
  }

  const tabs = [
    { id: 'active', label: 'Active', count: counts.active },
    { id: 'inactive', label: 'Inactive', count: counts.inactive },
    { id: 'deleted', label: 'Deleted', count: counts.deleted }
  ];

  if (loading && users.length === 0) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
      </div>
    );
  }

  return (
    <div class="team-page">
      {/* Header */}
      <div class="page-header">
        <div class="page-header-content">
          <h1>Team Members</h1>
          <p class="page-subtitle">Manage your team members, roles, and permissions</p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button class="btn btn-secondary" onClick={openBulkImportModal}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Bulk Import
            </button>
            <button class="btn btn-primary" onClick={openInviteModal}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Invite Member
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div class="team-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            class={`team-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span class="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div class="team-filters">
        <div class="filter-search">
          <svg class="search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            class="form-input"
            placeholder="Search by name, email or Slack ID..."
            value={filter.search}
            onInput={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>
        <div class="filter-selects">
          <select
            class="form-select"
            value={filter.department}
            onChange={(e) => setFilter({ ...filter, department: e.target.value })}
          >
            <option value="">All Departments</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            class="form-select"
            value={filter.location}
            onChange={(e) => setFilter({ ...filter, location: e.target.value })}
          >
            <option value="">All Locations</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <select
            class="form-select"
            value={filter.role}
            onChange={(e) => setFilter({ ...filter, role: e.target.value })}
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="approver">Approver</option>
            <option value="user">User</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div class="team-results-info">
        Showing {filteredUsers.length} of {users.length} {activeTab} users
      </div>

      {/* Team Grid */}
      <div class="team-grid">
        {filteredUsers.length > 0 ? (
          filteredUsers.map(member => (
            <div key={member.id} class="team-card">
              <div class="team-card-header">
                <div class="team-card-avatar">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt="" />
                  ) : (
                    <span>{member.name?.charAt(0) || '?'}</span>
                  )}
                  {activeTab === 'active' && (
                    <span class="status-indicator online" />
                  )}
                </div>
                <div class="team-card-info">
                  <h3 class="team-card-name">{member.name}</h3>
                  {member.display_name && (
                    <span class="team-card-slack">@{member.display_name}</span>
                  )}
                </div>
                {isAdmin && (
                  <div class="team-card-menu">
                    <button class="btn-icon" onClick={() => openEditModal(member)}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div class="team-card-body">
                <div class="team-card-detail">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{member.email || 'No email'}</span>
                </div>
                <div class="team-card-detail">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>{member.department_name || 'No department'}</span>
                </div>
                <div class="team-card-detail">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{member.location_name || 'No location'}</span>
                </div>
                {member.manager_name && (
                  <div class="team-card-detail">
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Reports to: {member.manager_name}</span>
                  </div>
                )}
              </div>

              {/* Leave Balances */}
              {userBalances[member.id] && leaveTypes.length > 0 && (
                <div class="team-card-balances" style={{
                  padding: '0.75rem 1rem',
                  borderTop: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--surface-bg, #f9fafb)'
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary, #6b7280)', marginBottom: '0.5rem' }}>
                    Leave Balances
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {leaveTypes.filter(lt => lt.is_active !== false).map(lt => {
                      const balance = userBalances[member.id]?.[lt.id];
                      if (!balance) return null;
                      const available = (balance.entitlement || 0) + (balance.carried_over || 0) + (balance.adjustment || 0) - (balance.used || 0);
                      const total = (balance.entitlement || 0) + (balance.carried_over || 0) + (balance.adjustment || 0);
                      const unitLabel = lt.unit === 'hours' ? 'h' : 'd';

                      return (
                        <div
                          key={lt.id}
                          title={`${lt.name}: ${available}${unitLabel} available of ${total}${unitLabel}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid var(--border-color, #e5e7eb)',
                            fontSize: '0.75rem'
                          }}
                        >
                          <span>{lt.icon || '📋'}</span>
                          <span style={{ fontWeight: '600', color: available > 0 ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)' }}>
                            {available}
                          </span>
                          <span style={{ color: 'var(--text-secondary, #9ca3af)' }}>
                            /{total}{unitLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div class="team-card-footer">
                <span class={`badge ${getRoleBadgeClass(member.role)}`}>
                  {member.role}
                </span>

                {isAdmin && activeTab === 'active' && (
                  <div class="team-card-actions">
                    <button class="btn btn-sm btn-secondary" onClick={() => openEditModal(member)}>
                      Edit
                    </button>
                    <button class="btn btn-sm btn-primary-outline" onClick={() => openToilModal(member)} title="Add Time Off In Lieu">
                      TOIL
                    </button>
                    <button class="btn btn-sm btn-danger-outline" onClick={() => handleDeactivate(member.id)}>
                      Deactivate
                    </button>
                    {member.role !== 'owner' && member.role !== 'admin' && (
                      <button class="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(member.id, member.name)} title="Permanently remove this user">
                        Remove
                      </button>
                    )}
                  </div>
                )}

                {isAdmin && activeTab === 'inactive' && (
                  <div class="team-card-actions">
                    <button class="btn btn-sm btn-primary" onClick={() => handleActivate(member.id)}>
                      Activate
                    </button>
                    <button class="btn btn-sm btn-danger-outline" onClick={() => handleDelete(member.id)}>
                      Delete
                    </button>
                  </div>
                )}

                {isAdmin && activeTab === 'deleted' && (
                  <div class="team-card-actions">
                    <button class="btn btn-sm btn-primary" onClick={() => handleRestore(member.id)}>
                      Restore
                    </button>
                    {user?.role === 'owner' && member.role !== 'owner' && (
                      <button class="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(member.id, member.name)}>
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div class="team-empty">
            <div class="empty-state-icon">
              {activeTab === 'active' ? '👥' : activeTab === 'inactive' ? '😴' : '🗑️'}
            </div>
            <h3>No {activeTab} team members found</h3>
            <p>
              {filter.search || filter.department || filter.location || filter.role
                ? 'Try adjusting your filters'
                : activeTab === 'active'
                  ? 'Invite team members to get started'
                  : activeTab === 'inactive'
                    ? 'No deactivated users'
                    : 'No deleted users'
              }
            </p>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <>
          <div class="modal-overlay" onClick={() => setShowEditModal(false)} />
          <div class="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h2>Edit Team Member</h2>
              <button class="btn-icon" onClick={() => setShowEditModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div class="edit-user-header">
                <div class="avatar avatar-lg">
                  {editingUser.avatar_url ? (
                    <img src={editingUser.avatar_url} alt="" />
                  ) : (
                    editingUser.name?.charAt(0) || '?'
                  )}
                </div>
                <div>
                  <h3>{editingUser.name}</h3>
                  <p>{editingUser.email}</p>
                </div>
              </div>

              <div class="form-group">
                <label>Role</label>
                <select
                  class="form-select"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="approver">Approver</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              </div>

              <div class="form-group">
                <label>Department</label>
                <select
                  class="form-select"
                  value={editingUser.department_id}
                  onChange={(e) => setEditingUser({ ...editingUser, department_id: e.target.value })}
                >
                  <option value="">No Department</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div class="form-group">
                <label>Location</label>
                <select
                  class="form-select"
                  value={editingUser.location_id}
                  onChange={(e) => setEditingUser({ ...editingUser, location_id: e.target.value })}
                >
                  <option value="">No Location</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div class="form-group">
                <label>Manager</label>
                <select
                  class="form-select"
                  value={editingUser.manager_id}
                  onChange={(e) => setEditingUser({ ...editingUser, manager_id: e.target.value })}
                >
                  <option value="">No Manager</option>
                  {allUsers.filter(u => u.id !== editingUser.id && u.is_active).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div class="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  class="form-input"
                  value={editingUser.start_date?.split('T')[0] || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, start_date: e.target.value })}
                />
              </div>

              {/* Custom Working Days Override */}
              <div class="form-group" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingUser.use_custom_working_days}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Enable custom working days - default to department or Mon-Fri
                        setEditingUser({
                          ...editingUser,
                          use_custom_working_days: true,
                          working_days: editingUser.working_days || [1, 2, 3, 4, 5]
                        });
                      } else {
                        // Disable - will use department default
                        setEditingUser({
                          ...editingUser,
                          use_custom_working_days: false,
                          working_days: null
                        });
                      }
                    }}
                  />
                  <span style={{ fontWeight: '500' }}>Custom Working Days</span>
                </label>
                <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                  Override the department's default working days for this user (e.g., part-time schedule)
                </small>
              </div>

              {editingUser.use_custom_working_days && (
                <div class="form-group">
                  <label>Working Days</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {dayNames.map((day, idx) => (
                      <label
                        key={day}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '48px',
                          height: '40px',
                          border: '2px solid',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                          borderColor: editingUser.working_days?.includes(idx) ? '#3b82f6' : '#e5e7eb',
                          background: editingUser.working_days?.includes(idx) ? '#eff6ff' : 'white',
                          color: editingUser.working_days?.includes(idx) ? '#3b82f6' : '#6b7280',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editingUser.working_days?.includes(idx) || false}
                          onChange={(e) => {
                            const newDays = e.target.checked
                              ? [...(editingUser.working_days || []), idx].sort((a, b) => a - b)
                              : (editingUser.working_days || []).filter(d => d !== idx);
                            setEditingUser({ ...editingUser, working_days: newDays });
                          }}
                          style={{ display: 'none' }}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                    Select which days this employee works (leave requests will only count these days)
                  </small>
                </div>
              )}
            </div>
            <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
              <button class="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button class="btn btn-primary" onClick={handleSaveUser} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* TOIL Modal */}
      {showToilModal && toilUser && (
        <>
          <div class="modal-overlay" onClick={() => setShowToilModal(false)} />
          <div class="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h2>Manage TOIL</h2>
              <button class="btn-icon" onClick={() => setShowToilModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div class="edit-user-header">
                <div class="avatar avatar-lg">
                  {toilUser.avatar_url ? (
                    <img src={toilUser.avatar_url} alt="" />
                  ) : (
                    toilUser.name?.charAt(0) || '?'
                  )}
                </div>
                <div>
                  <h3>{toilUser.name}</h3>
                  <p>Time Off In Lieu (TOIL)</p>
                </div>
              </div>

              {/* Current TOIL Balance */}
              <div class="toil-balance-card" style="background: var(--surface-bg); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
                <h4 style="margin: 0 0 0.5rem 0; font-size: 0.875rem; color: var(--text-secondary);">Current TOIL Balance</h4>
                {toilBalance ? (
                  <div style="display: flex; gap: 1.5rem;">
                    <div>
                      <span style="font-size: 1.5rem; font-weight: 600; color: var(--primary);">{toilBalance.toil_available || 0}h</span>
                      <span style="color: var(--text-secondary); font-size: 0.875rem; margin-left: 0.25rem;">available</span>
                    </div>
                    <div>
                      <span style="font-size: 1rem; color: var(--text-secondary);">{toilBalance.toil_hours || 0}h granted</span>
                    </div>
                    <div>
                      <span style="font-size: 1rem; color: var(--text-secondary);">{toilBalance.toil_used || 0}h used</span>
                    </div>
                  </div>
                ) : (
                  <p style="color: var(--text-secondary); margin: 0;">Loading...</p>
                )}
              </div>

              <div class="form-group">
                <label>Hours to Add</label>
                <input
                  type="number"
                  class="form-input"
                  step="0.5"
                  min="0.5"
                  placeholder="e.g., 4 or 8"
                  value={toilForm.hours}
                  onInput={(e) => setToilForm({ ...toilForm, hours: e.target.value })}
                />
                <small style="color: var(--text-secondary);">8 hours = 1 day of TOIL</small>
              </div>

              <div class="form-group">
                <label>Reason</label>
                <textarea
                  class="form-input"
                  rows="2"
                  placeholder="e.g., Worked Saturday for website launch"
                  value={toilForm.reason}
                  onInput={(e) => setToilForm({ ...toilForm, reason: e.target.value })}
                />
              </div>

              <div class="alert alert-info" style="background: #e8f4fd; padding: 0.75rem 1rem; border-radius: 6px; font-size: 0.875rem;">
                <strong>Note:</strong> TOIL hours are added to the user's vacation balance but tracked separately. TOIL does NOT carry over to the next year.
              </div>
            </div>
            <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
              <button class="btn btn-secondary" onClick={() => setShowToilModal(false)}>
                Close
              </button>
              <button class="btn btn-primary" onClick={handleAddToil} disabled={addingToil || !toilForm.hours}>
                {addingToil ? 'Adding...' : 'Add TOIL Hours'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowInviteModal(false)} />
          <div class="modal" style="max-width: 600px; max-height: 90vh; display: flex; flex-direction: column;">
            <div class="modal-header" style="flex-shrink: 0;">
              <h2>Add Team Member</h2>
              <button class="btn-icon" onClick={() => setShowInviteModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style="flex: 1; overflow-y: auto; min-height: 0;">
              <p style="margin-bottom: 1rem; color: var(--text-secondary);">
                Search for Slack members who haven't been added to the vacation tracker yet.
                This includes guest members (single-channel and multi-channel guests).
              </p>

              <div class="filter-search" style="margin-bottom: 1rem;">
                <svg class="search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  class="form-input"
                  placeholder="Search by name or email..."
                  value={inviteSearch}
                  onInput={(e) => handleInviteSearch(e.target.value)}
                />
              </div>

              <div class="invite-results" style="max-height: 400px; overflow-y: auto;">
                {inviteSearching ? (
                  <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                    <div class="spinner" style="margin: 0 auto 0.5rem;" />
                    Searching Slack users...
                  </div>
                ) : inviteSearchResults.length === 0 ? (
                  <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                    <div style="font-size: 2rem; margin-bottom: 0.5rem;">👥</div>
                    {inviteSearch
                      ? 'No matching Slack users found'
                      : 'All Slack users have been added already'
                    }
                  </div>
                ) : (
                  <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                    {inviteSearchResults.map(slackUser => (
                      <div
                        key={slackUser.id}
                        class="invite-user-row"
                        style="display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px;"
                      >
                        <div class="avatar" style="width: 40px; height: 40px; flex-shrink: 0;">
                          {slackUser.avatar_url ? (
                            <img src={slackUser.avatar_url} alt="" style="width: 100%; height: 100%; border-radius: 50%;" />
                          ) : (
                            <span style="width: 100%; height: 100%; border-radius: 50%; background: var(--primary); color: white; display: flex; align-items: center; justify-content: center;">
                              {slackUser.name?.charAt(0) || '?'}
                            </span>
                          )}
                        </div>
                        <div style="flex: 1; min-width: 0;">
                          <div style="display: flex; align-items: center; gap: 0.5rem;">
                            <span style="font-weight: 500;">{slackUser.name}</span>
                            {slackUser.is_guest && (
                              <span
                                class="badge"
                                style="font-size: 0.7rem; padding: 0.15rem 0.4rem; background: #fef3c7; color: #92400e;"
                              >
                                {slackUser.guest_type === 'single_channel' ? 'Single-Channel Guest' : 'Multi-Channel Guest'}
                              </span>
                            )}
                          </div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                            {slackUser.email || slackUser.display_name || slackUser.id}
                          </div>
                        </div>
                        <button
                          class="btn btn-sm btn-primary"
                          onClick={() => handleImportUser(slackUser)}
                          disabled={importing === slackUser.id}
                          style="flex-shrink: 0;"
                        >
                          {importing === slackUser.id ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div class="modal-footer" style="flex-shrink: 0; background: white;">
              <button class="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                Close
              </button>
            </div>
          </div>
        </>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowBulkImportModal(false)} />
          <div class="modal" style="max-width: 550px; max-height: 90vh; display: flex; flex-direction: column;">
            <div class="modal-header" style="flex-shrink: 0;">
              <h2>Bulk Import from Slack</h2>
              <button class="btn-icon" onClick={() => setShowBulkImportModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style="flex: 1; overflow-y: auto; min-height: 0;">
              <p style="margin-bottom: 1.5rem; color: var(--text-secondary);">
                Import team members from your Slack workspace. Choose which types of members to include.
              </p>

              {bulkImportLoading ? (
                <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                  <div class="spinner" style="margin: 0 auto 0.5rem;" />
                  Loading Slack workspace...
                </div>
              ) : bulkImportCounts ? (
                <>
                  {/* Member counts */}
                  <div style="background: var(--surface-bg); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
                    <h4 style="margin: 0 0 0.75rem 0; font-size: 0.875rem; color: var(--text-secondary);">Slack Workspace Members</h4>
                    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; text-align: center;">
                      <div>
                        <div style="font-size: 1.5rem; font-weight: 600; color: var(--primary);">{bulkImportCounts.slack.full}</div>
                        <div style="font-size: 0.75rem; color: var(--text-secondary);">Full Members</div>
                      </div>
                      <div>
                        <div style="font-size: 1.5rem; font-weight: 600; color: var(--warning, #f59e0b);">{bulkImportCounts.slack.multiChannel}</div>
                        <div style="font-size: 0.75rem; color: var(--text-secondary);">Multi-Channel Guests</div>
                      </div>
                      <div>
                        <div style="font-size: 1.5rem; font-weight: 600; color: var(--text-secondary);">{bulkImportCounts.slack.singleChannel}</div>
                        <div style="font-size: 0.75rem; color: var(--text-secondary);">Single-Channel Guests</div>
                      </div>
                    </div>
                    {bulkImportCounts.imported > 0 && (
                      <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid var(--border-color); font-size: 0.875rem; color: var(--text-secondary);">
                        Already imported: {bulkImportCounts.imported} users
                      </div>
                    )}
                  </div>

                  {/* Filter options */}
                  <div class="form-group" style="margin-bottom: 1.5rem;">
                    <label style="margin-bottom: 0.5rem; display: block; font-weight: 500;">Who do you want to import?</label>
                    <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                      <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s;" class={bulkImportFilter === 'all' ? 'selected' : ''}>
                        <input
                          type="radio"
                          name="guestFilter"
                          value="all"
                          checked={bulkImportFilter === 'all'}
                          onChange={() => setBulkImportFilter('all')}
                        />
                        <div>
                          <div style="font-weight: 500;">All members ({bulkImportCounts.slack.total})</div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary);">Full members + all guest types</div>
                        </div>
                      </label>
                      <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s;">
                        <input
                          type="radio"
                          name="guestFilter"
                          value="include_multi"
                          checked={bulkImportFilter === 'include_multi'}
                          onChange={() => setBulkImportFilter('include_multi')}
                        />
                        <div>
                          <div style="font-weight: 500;">Full members + multi-channel guests ({bulkImportCounts.slack.full + bulkImportCounts.slack.multiChannel})</div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary);">Excludes single-channel guests</div>
                        </div>
                      </label>
                      <label style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--surface-bg); border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s;">
                        <input
                          type="radio"
                          name="guestFilter"
                          value="full_only"
                          checked={bulkImportFilter === 'full_only'}
                          onChange={() => setBulkImportFilter('full_only')}
                        />
                        <div>
                          <div style="font-weight: 500;">Full members only ({bulkImportCounts.slack.full})</div>
                          <div style="font-size: 0.8rem; color: var(--text-secondary);">Excludes all guest types</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Import result */}
                  {bulkImportResult && (
                    <div style={`padding: 1rem; border-radius: 8px; margin-bottom: 1rem; ${bulkImportResult.error ? 'background: #fef2f2; color: #991b1b;' : 'background: #f0fdf4; color: #166534;'}`}>
                      {bulkImportResult.error ? (
                        <strong>Error: {bulkImportResult.error}</strong>
                      ) : (
                        <>
                          <strong>Import complete!</strong>
                          <div style="margin-top: 0.5rem;">
                            {bulkImportResult.created} new users imported, {bulkImportResult.updated} existing users updated
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
                  Failed to load Slack workspace data
                </div>
              )}
            </div>
            <div class="modal-footer" style="flex-shrink: 0; background: white;">
              <button class="btn btn-secondary" onClick={() => setShowBulkImportModal(false)}>
                {bulkImportResult?.created > 0 ? 'Done' : 'Cancel'}
              </button>
              {bulkImportCounts && !bulkImportResult?.created && (
                <button
                  class="btn btn-primary"
                  onClick={handleBulkImport}
                  disabled={bulkImporting || getImportCount() === 0}
                >
                  {bulkImporting ? 'Importing...' : `Import ${getImportCount()} Users`}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function getRoleBadgeClass(role) {
  switch (role) {
    case 'owner':
      return 'badge-owner';
    case 'admin':
      return 'badge-admin';
    case 'approver':
      return 'badge-approver';
    default:
      return 'badge-user';
  }
}
